import { Link } from "react-router-dom";
import { SeoPageShell } from "./SeoPageShell";

const FAQS = [
  ["Do I need permission to visit a fire station?", "Yes, in most cases. Contact the department's administrative office or the station directly and ask about their visitor policy. Some departments welcome candidate visits; others restrict them, especially during active hiring or for security reasons. Respect whatever answer you get."],
  ["What should I bring to a station visit?", "Very little. Yourself, a notebook, and possibly a copy of your resume in case someone asks. Do not bring gifts or food unless the department has a known tradition and you have confirmed it is appropriate."],
  ["Should I mention station visits in my oral board?", "If you visited and learned something specific, yes — briefly and accurately. Panels generally respond well to candidates who took initiative to understand the department. Do not exaggerate the visit or imply relationships you do not have."],
  ["How do I find stations and department information?", "Start with the department's official website. First Step's free fire department directory lists basic information and official links for departments across the United States, and researched profiles add publicly reported history and hiring notes."],
];

export default function FirefighterStationVisitQuestions() {
  return (
    <SeoPageShell
      path="/firefighter-station-visit-questions"
      title="Firefighter Station Visit Questions: What to Ask & How to Prepare"
      description="How to prepare for a fire station visit as a candidate: who to contact, professional etiquette, good questions to ask the crew, what to avoid, and how to follow up."
      h1="Firefighter Station Visit Questions"
      intro="A station visit is one of the best ways to learn what a department is really like — and to show initiative before an interview. Done well, it leaves crews with a good impression. Done poorly, it can do the opposite. Here is how to prepare, how to behave, and what to ask."
      faqs={FAQS}
      testId="seo-station-visit-page"
      cta="Create a free account to search the fire department directory, set your target department on your Career Road Map, and prepare for the interview that follows. Department-specific preparation tools are part of Premium."
    >
      <section>
        <h2>What a station visit is — and is not</h2>
        <p>A candidate station visit is a short, informal conversation with on-duty firefighters at a fire station, usually to learn about the department and the job. It is not a formal part of most hiring processes, and it is not an interview. The crew may be called away at any moment, and they are on duty. Your goal is simple: learn something useful, be respectful of their time, and leave a professional impression.</p>
        <p>Policies vary widely. Some departments encourage visits, some allow them only at certain times, and some do not permit them at all. Always check before you go.</p>
      </section>

      <section>
        <h2>Before you go</h2>
        <ol>
          <li><strong>Research the department first.</strong> Know its service area, number of stations and any public information about its hiring process. Use the department's official website and the free <Link to="/departments">fire department directory</Link>; California candidates can browse the <Link to="/departments/CA">California department directory</Link>. You should arrive with questions the website cannot answer.</li>
          <li><strong>Ask permission.</strong> Call the department's administrative line or the station and ask whether candidate visits are allowed and when a good time would be. Mid-morning after equipment checks, or early evening, are commonly suggested — but let the crew tell you.</li>
          <li><strong>Pick a low-key time.</strong> Avoid shift change, meal times and training periods unless invited.</li>
          <li><strong>Prepare a short introduction.</strong> Who you are, where you are in the hiring process, and why you are interested in their department — in about thirty seconds.</li>
          <li><strong>Dress neatly.</strong> Clean, conservative casual clothing is usually appropriate. You are not there in uniform, and you are not there in gym clothes.</li>
        </ol>
      </section>

      <section>
        <h2>Station visit etiquette</h2>
        <ul>
          <li>Arrive at the time you agreed to, and knock or use the public entrance. Never walk into the apparatus bay or living areas uninvited.</li>
          <li>Introduce yourself to the officer or senior firefighter first and ask if it is still a good time.</li>
          <li>Keep it short — often twenty to thirty minutes is plenty unless the crew invites you to stay.</li>
          <li>If the tones drop, step back, stay out of the way, and leave. Thank them later; they will understand.</li>
          <li>Do not touch equipment or apparatus unless invited.</li>
          <li>Listen more than you talk. This is not the time to recite your resume.</li>
          <li>Be respectful to everyone regardless of rank or role.</li>
          <li>Do not ask for favors, references or "a word" with the chief.</li>
          <li>Thank the crew before you leave and, if appropriate, send a brief thank-you note afterward.</li>
        </ul>
      </section>

      <section>
        <h2>Good questions to ask the crew</h2>
        <p>Ask about the work and the culture — the things you cannot learn from a website. Choose a handful; you do not need to ask all of them.</p>

        <h3>About the job and the department</h3>
        <ul>
          <li>What does a typical shift look like at this station?</li>
          <li>What types of calls are most common in this area?</li>
          <li>How is training handled — daily drills, department-wide training, outside courses?</li>
          <li>What do you enjoy most about working here? What is the hardest part?</li>
          <li>How does the department handle EMS — first response, transport, or both?</li>
        </ul>

        <h3>About preparing as a candidate</h3>
        <ul>
          <li>What do you wish you had known when you were going through the hiring process?</li>
          <li>What separates candidates who do well in probation from those who struggle?</li>
          <li>Are there certifications or experiences that helped you or your coworkers most?</li>
          <li>How can a candidate best learn about this community?</li>
        </ul>

        <h3>About culture and expectations</h3>
        <ul>
          <li>How does a new firefighter earn the trust of a crew here?</li>
          <li>What are the expectations of a probationary firefighter around the station?</li>
          <li>How does the department support ongoing education or promotion?</li>
        </ul>
      </section>

      <section>
        <h2>Questions and topics to avoid</h2>
        <ul>
          <li>Pay, overtime, schedules and time off as your first questions. They are legitimate topics, but a visit is not the place to lead with them.</li>
          <li>Anything about the current hiring list, scores, or "who to talk to."</li>
          <li>Department politics, labor disputes, discipline or recent controversies.</li>
          <li>Personal opinions of the chief or other members.</li>
          <li>Details of specific incidents involving patients or victims.</li>
        </ul>
      </section>

      <section>
        <h2>After the visit</h2>
        <ul>
          <li>Write down what you learned while it is fresh — names, details about the station, anything that surprised you.</li>
          <li>Send a short thank-you note or email if you have an appropriate contact.</li>
          <li>Fold what you learned into your interview answers, accurately. "When I visited Station 3, a crew member described…" is a strong, honest detail — as long as it is true.</li>
          <li>If you visited a department you decided is not right for you, that is a useful outcome too.</li>
        </ul>
      </section>

      <section>
        <h2>Station visit checklist</h2>
        <ul>
          <li>Department researched; questions the website cannot answer written down.</li>
          <li>Permission and timing confirmed.</li>
          <li>Thirty-second introduction ready.</li>
          <li>Neat, conservative clothing.</li>
          <li>Notebook; no gifts unless confirmed appropriate.</li>
          <li>Plan to keep it under thirty minutes and to leave immediately if the crew gets a call.</li>
          <li>Thank-you sent within a day or two.</li>
        </ul>
        <p>Once you know the department, put that knowledge to work: our guide on <Link to="/how-to-prepare-firefighter-interview">how to prepare for a firefighter interview</Link> shows where department research fits into your answers.</p>
      </section>
    </SeoPageShell>
  );
}
